import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { AlertTriangle, CheckCircle2, Copy, Loader2, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useUpdateCampaign, usePreviewCampaignMapping } from '@/api/campaigns/use-campaigns';
import type { Campaign } from '@/api/campaigns/types';
import { getCampaignStatusActions } from '@/lib/campaignStatus';
import { extractErrorMessages } from '@/components/campaigns/extractErrorMessages';

export interface ReviewStepProps {
    campaign: Campaign;
    onBack: () => void;
}

/** Builds the preview sample from the campaign's captured/declared source field samples. */
function buildSampleFromSourceFields(campaign: Campaign): Record<string, unknown> {
    return campaign.source_fields.reduce<Record<string, unknown>>((acc, field) => {
        if (field.sample !== undefined && field.sample !== null && field.sample !== '') {
            acc[field.key] = field.sample;
        }
        return acc;
    }, {});
}

const ReviewStep: React.FC<ReviewStepProps> = ({ campaign, onBack }) => {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const previewMutation = usePreviewCampaignMapping();
    const updateCampaign = useUpdateCampaign();

    const [preview, setPreview] = useState<{
        mapped_data: Record<string, unknown>;
        warnings: string[];
        errors: string[];
    } | null>(null);
    const [activationErrors, setActivationErrors] = useState<string[]>([]);

    const { canActivate } = getCampaignStatusActions(campaign.status);
    const mappedFieldsCount = campaign.field_mapping?.fields.length ?? 0;

    const sample = useMemo(() => buildSampleFromSourceFields(campaign), [campaign]);

    const handlePreview = async () => {
        try {
            const result = await previewMutation.mutateAsync({
                id: campaign.id,
                data: { sample },
            });
            setPreview(result);
        } catch {
            enqueueSnackbar(t('campaigns.editor.review.previewFailed'), { variant: 'error' });
        }
    };

    const copyEndpoint = async () => {
        if (!campaign.endpoint_url) return;
        try {
            await navigator.clipboard.writeText(campaign.endpoint_url);
            enqueueSnackbar(t('campaigns.editor.fields.urlCopied'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('campaigns.editor.fields.copyFailed'), { variant: 'error' });
        }
    };

    const handleActivate = async () => {
        setActivationErrors([]);
        try {
            await updateCampaign.mutateAsync({ id: campaign.id, data: { status: 'active' } });
            enqueueSnackbar(t('campaigns.editor.review.activated'), { variant: 'success' });
        } catch (error) {
            const body = (error as { response?: { data?: unknown } })?.response?.data;
            const messages = extractErrorMessages(body);
            setActivationErrors(
                messages.length > 0 ? messages : [t('campaigns.editor.review.activateFailed')]
            );
            enqueueSnackbar(t('campaigns.editor.review.activateFailed'), { variant: 'error' });
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{t('campaigns.editor.review.summaryTitle')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.editor.source.name')}
                        </span>
                        <span className="font-medium">{campaign.name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.columns.source')}
                        </span>
                        <Badge variant="outline">{t(`campaigns.source.${campaign.source}`)}</Badge>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.editor.review.fieldsMapped')}
                        </span>
                        <span className="font-medium">{mappedFieldsCount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.editor.defaults.statusLabel')}
                        </span>
                        <span className="font-medium">
                            {campaign.default_lead_status?.label ??
                                t('campaigns.editor.review.systemDefault')}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.editor.defaults.assigneeLabel')}
                        </span>
                        <span className="font-medium">
                            {campaign.default_assignee?.name ?? t('campaigns.editor.review.none')}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                            {t('campaigns.editor.mapping.defaultCountry')}
                        </span>
                        <span className="font-medium">
                            {campaign.default_country ?? t('campaigns.editor.review.none')}
                        </span>
                    </div>

                    {campaign.endpoint_url && (
                        <div className="space-y-2 pt-2">
                            <span className="text-sm text-muted-foreground">
                                {t('campaigns.editor.fields.endpointTitle')}
                            </span>
                            <div className="flex items-center gap-2">
                                <Input
                                    readOnly
                                    value={campaign.endpoint_url}
                                    className="font-mono text-xs"
                                />
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    onClick={copyEndpoint}
                                    aria-label={t('campaigns.editor.fields.copyUrl')}
                                >
                                    <Copy className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                    <CardTitle className="text-base">
                        {t('campaigns.editor.review.previewTitle')}
                    </CardTitle>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreview}
                        disabled={previewMutation.isPending}
                    >
                        {previewMutation.isPending && (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        )}
                        {t('campaigns.editor.review.previewButton')}
                    </Button>
                </CardHeader>
                <CardContent className="space-y-3">
                    {!preview && (
                        <p className="text-sm text-muted-foreground">
                            {t('campaigns.editor.review.previewHint')}
                        </p>
                    )}
                    {preview && (
                        <>
                            <div className="divide-y rounded-lg border">
                                {Object.entries(preview.mapped_data).map(([key, value]) => (
                                    <div
                                        key={key}
                                        className="flex items-center justify-between p-2 text-sm"
                                    >
                                        <span className="text-muted-foreground">{key}</span>
                                        <span className="truncate pl-4 text-right font-medium">
                                            {typeof value === 'object'
                                                ? JSON.stringify(value)
                                                : String(value)}
                                        </span>
                                    </div>
                                ))}
                                {Object.keys(preview.mapped_data).length === 0 && (
                                    <p className="p-3 text-sm text-muted-foreground">
                                        {t('campaigns.editor.review.previewEmpty')}
                                    </p>
                                )}
                            </div>
                            {preview.warnings.length > 0 && (
                                <div className="space-y-1">
                                    {preview.warnings.map((warning, index) => (
                                        <p
                                            key={index}
                                            className="text-sm text-amber-600 dark:text-amber-400"
                                        >
                                            • {warning}
                                        </p>
                                    ))}
                                </div>
                            )}
                            {preview.errors.length > 0 && (
                                <div className="space-y-1">
                                    {preview.errors.map((error, index) => (
                                        <p
                                            key={index}
                                            className="text-sm text-red-600 dark:text-red-400"
                                        >
                                            • {error}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {activationErrors.length > 0 && (
                <Card>
                    <CardContent className="space-y-2 pt-6">
                        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="text-sm font-medium">
                                {t('campaigns.editor.review.activateFailed')}
                            </span>
                        </div>
                        {activationErrors.map((error, index) => (
                            <p key={index} className="text-sm text-red-600 dark:text-red-400">
                                • {error}
                            </p>
                        ))}
                    </CardContent>
                </Card>
            )}

            <div className="flex items-center justify-between border-t pt-4">
                <Button variant="outline" onClick={onBack}>
                    {t('campaigns.editor.actions.back')}
                </Button>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => navigate(`/campaigns/${campaign.id}`)}>
                        {t('campaigns.editor.actions.saveAndExit')}
                    </Button>
                    {canActivate && (
                        <Button onClick={handleActivate} disabled={updateCampaign.isPending}>
                            {updateCampaign.isPending ? (
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                                <PlayCircle className="h-4 w-4 mr-2" />
                            )}
                            {t('campaigns.editor.review.activate')}
                        </Button>
                    )}
                    {!canActivate && campaign.status === 'active' && (
                        <Badge variant="outline" className="gap-1">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {t('campaigns.editor.review.alreadyActive')}
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ReviewStep;
